import React, { useCallback, useEffect } from "react";

import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Divider, Stack, Typography } from "@mui/material";
import { InlineFilter } from "components/Filters/InlineFilter";
import { SelectDateRange } from "components/forms/Select-date-range/SelectDateRange";
import { RoleComponentGuard } from "components/guards";
import { useActionHook, useModals } from "hooks";
import { useReportsActions, useReportsFiltersSelector, useUserRoleSelector } from "store";
import { DateRange, DateRangeValueType, DocumentReportQueries } from "store/types";

import { RoleName } from "../../../constants";
import { useContentContext } from "../Content/content-context";
import { SelectCurrency } from "./SelectCurrency";

const isInTheFuture = (date: string): boolean => {
  const today = new Date();
  today.setHours(23, 59, 59, 998);
  return new Date(date) > today;
};

export const ReportQueryManager: React.FC<DocumentReportQueries> = (props) => {
  const { dateRange, currency, filters } = props;
  const stateFilters = useReportsFiltersSelector();
  const { getFilters } = useReportsActions();
  const { data: filtersList, action: getFiltersAction } = useActionHook(async () => getFilters(stateFilters));
  const { updateDocumentReportQueries } = useContentContext();
  const role = useUserRoleSelector();
  const isReadOnlyUser =
    role?.name === RoleName.Guest || role?.name === RoleName.Viewer || role?.name === RoleName.TeamViewer;

  const { errorModal, noticeModal } = useModals();

  const handleChangeQueries = useCallback(
    (queries: Partial<DocumentReportQueries>) => {
      const updatedQueries = { ...props, ...queries };
      updateDocumentReportQueries(updatedQueries);
    },
    [props, updateDocumentReportQueries]
  );

  const handleDateRangeChange = useCallback(
    (data: DateRange & { value: DateRangeValueType }) => {
      const { to, from } = data;
      if ((to && isInTheFuture(to)) || (from && isInTheFuture(from))) {
        noticeModal({
          msg: "Notice - Report can not show future values of different currency. Different currency's in future dates are ignored."
        });
      }

      if (to && from && from > to) {
        // eslint-disable-next-line quotes
        return errorModal({ msg: '"Date From" must be less than "Date To"' });
      }

      handleChangeQueries({ dateRange: data });
    },
    [handleChangeQueries]
  );

  useEffect(() => {
    getFiltersAction();
  }, []);

  return (
    <Stack
      flexDirection="column"
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      sx={{
        width: "100%",
        bgcolor: "white",
        alignItems: "stretch",
        maxHeight: "140px",
        overflow: "auto",
        gap: "12px",
        py: "15px"
      }}
    >
      <Stack alignItems="flex-start" justifyContent="space-between" flexDirection="row">
        <Typography sx={{ color: "#071F35", fontSize: "20px", fontWeight: "500", lineHeight: 1 }}>Report</Typography>
        <Stack gap="10px" flexDirection="row">
          <SelectDateRange
            disabled={isReadOnlyUser}
            formControlProps={{ sx: { minWidth: "200px" } }}
            selectProps={{
              startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />
            }}
            label=""
            value={dateRange}
            onChange={handleDateRangeChange}
          />
          <SelectCurrency
            disabled={isReadOnlyUser}
            currency={currency}
            onChange={(currency) => handleChangeQueries({ currency })}
          />
        </Stack>
      </Stack>
      <Divider sx={{ width: "100%", bgcolor: "#DBE8F5", borderColor: "#DBE8F5" }} />
      <Stack>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
          <InlineFilter
            state={filters}
            filtersList={filtersList || {}}
            handleChangeFilter={(filters) => {
              handleChangeQueries({ filters });
            }}
            applyMode
          />
        </RoleComponentGuard>
      </Stack>
    </Stack>
  );
};
